import { getExtensionValue } from "@medplum/core";
import {
  CodeableConcept,
  Questionnaire,
  QuestionnaireItem,
  QuestionnaireResponseItem,
} from "@medplum/fhirtypes";

import { UseFormReturnType } from "@mantine/form";

import isBoolean from "lodash/isBoolean";
import isNumber from "lodash/isNumber";

import { Extensions } from "@/fhir/enums/extensions";
import { getQuestionnaireResponseItems } from "@/utils/medplum";
import { GenericFormValues, getFormsetCodeLinkId } from "@/utils/questionnaires";

import { INTAKE_FORM_INITIAL_VALUES, VALUE_TYPE_PARSERS } from "../constants/intake-forms";

const parseInitialData = (valueType: string, value: string) => {
  const parser = VALUE_TYPE_PARSERS[valueType] || (() => "");
  return value || isNumber(value) || isBoolean(value) ? parser(value) : "";
};

const parseFormsetInitialValues = (
  item: QuestionnaireItem,
  responseItems: QuestionnaireResponseItem[],
) => {
  return responseItems.reduce<Record<string, unknown>>((result, nestedItem, index) => {
    const fieldName = getFormsetCodeLinkId(nestedItem.item);
    const codeValue = nestedItem.item?.find((i) => i.linkId === fieldName)?.answer?.[0]
      ?.valueString;
    const field = codeValue?.trim() ? codeValue : index.toString();
    return {
      ...result,
      [field]: createFormInitialValues(item.item!, nestedItem.item ?? []),
    };
  }, {});
};

const parseControlledFormsetInitialValues = (
  item: QuestionnaireItem,
  responseItems: QuestionnaireResponseItem[],
) => {
  return responseItems.reduce((result: GenericFormValues, nestedItem) => {
    const fieldName = getFormsetCodeLinkId(nestedItem.item);
    const field = nestedItem.item?.find((item) => item.linkId === fieldName)?.answer?.[0]
      ?.valueString;
    if (!field) return result;

    const currentResult = result[field] ?? {};
    const currentLength = Object.keys(currentResult).length.toString();
    return {
      ...result,
      [field]: {
        ...currentResult,
        [currentLength]: createFormInitialValues(item.item!, nestedItem.item ?? []),
      },
    };
  }, {});
};

export const createFormInitialValues = (
  questionnaireItems: QuestionnaireItem[],
  questionnaireResponseItems: QuestionnaireResponseItem[] = [],
): Record<string, unknown> => {
  return questionnaireItems.reduce((acc: Record<string, unknown>, item) => {
    const responseItems = getQuestionnaireResponseItems(
      item.linkId,
      questionnaireResponseItems,
      item.repeats,
    );

    const isFormset = !!(item.repeats && item.item);
    const isNestedFormset = !!(
      isFormset &&
      getExtensionValue(item, Extensions.QuestionnaireItemControlledFormset) &&
      getExtensionValue(item, Extensions.QuestionnaireItemFormset)
    );

    if (isNestedFormset) {
      return {
        ...acc,
        [item.linkId]: {
          ...(acc[item.linkId] ?? {}),
          ...parseControlledFormsetInitialValues(item, responseItems),
        },
      };
    }

    if (isFormset) {
      return {
        ...acc,
        [item.linkId]: {
          ...(acc[item.linkId] ?? {}),
          ...parseFormsetInitialValues(item, responseItems),
        },
      };
    }

    if (item.type === "group" && item.item) {
      const isHorizontal = item.extension?.some(
        (ext) => ext.valueCodeableConcept?.coding?.[0]?.code === "horizontal-group",
      );

      if (isHorizontal) {
        const horizontalValues = item.item.reduce((values, child) => {
          const childResponse = responseItems?.[0]?.item?.find(
            (resp) => resp.linkId === child.linkId,
          );

          if (child.type === "group" && child.item) {
            const nestedValues = createFormInitialValues(
              child.item,
              childResponse?.item ?? responseItems?.[0]?.item ?? [],
            );

            if (Object.keys(nestedValues).length > 0) {
              return {
                ...values,
                [child.linkId]: nestedValues,
              };
            }

            return values;
          }

          if (!childResponse?.answer?.[0]) return values;

          try {
            const [valueType, value] = Object.entries(childResponse.answer[0])[0];
            const parsedValue = parseInitialData(valueType, value);

            if (parsedValue !== undefined && parsedValue !== "") {
              return {
                ...values,
                [child.linkId]: parsedValue,
              };
            }
            return values;
          } catch (err) {
            console.warn(`Error parsing value for ${child.linkId}:`, err);
            return values;
          }
        }, {});

        return {
          ...acc,
          [item.linkId]: horizontalValues,
        };
      }

      return {
        ...acc,
        [item.linkId]: createFormInitialValues(item.item, responseItems?.[0]?.item ?? []),
      };
    }

    // Add this new section to handle horizontal groups within formsets
    const isChildOfFormset = questionnaireItems.some(
      (parent) => parent.repeats && parent.item?.some((child) => child.linkId === item.linkId),
    );

    if (
      isChildOfFormset &&
      item.type === "group" &&
      item.extension?.some(
        (ext) => ext.valueCodeableConcept?.coding?.[0]?.code === "horizontal-group",
      )
    ) {
      // Initialize an empty object for the horizontal group
      return {
        ...acc,
        [item.linkId]: {},
      };
    }

    const isChildOfHorizontal = questionnaireItems.some(
      (parent) =>
        parent.extension?.some(
          (ext) => ext.valueCodeableConcept?.coding?.[0]?.code === "horizontal-group",
        ) && parent.item?.some((child) => child.linkId === item.linkId),
    );

    if (isChildOfHorizontal) {
      return acc;
    }

    const answers = responseItems?.[0]?.answer?.length
      ? responseItems[0].answer.map((answer) => {
          const entries = Object.entries(answer || {});
          const [valueType, value] = entries[0] || [];
          return parseInitialData(valueType, value);
        })
      : undefined;

    const isNativeSelect =
      (getExtensionValue(item, Extensions.QuestionnaireItemControl) as CodeableConcept)?.coding?.[0]
        ?.code === "native-select";

    const isSwitch =
      (getExtensionValue(item, Extensions.QuestionnaireItemControl) as CodeableConcept)?.coding?.[0]
        ?.code === "switch";

    const isSignatureSelect =
      (getExtensionValue(item, Extensions.QuestionnaireItemControl) as CodeableConcept)?.coding?.[0]
        ?.code === "signature-select";

    let initialValue;
    if (getExtensionValue(item, Extensions.QuestionnaireItemFormsetController)) {
      initialValue = answers?.map((answer) => JSON.stringify(answer));
    } else if (isNativeSelect) {
      initialValue = answers?.[0] ?? INTAKE_FORM_INITIAL_VALUES[item.type] ?? "";
    } else if (isSignatureSelect) {
      // MultiSelect always holds an array. Without this, a single stored signer would hydrate as a
      // bare string and the field would render empty on edit.
      initialValue = answers ?? [];
    } else if (answers && answers.length > 1) {
      initialValue = answers;
    } else if (item.type === "boolean") {
      initialValue = answers?.[0];
    } else if (isSwitch && !answers?.[0] && item.answerOption && item.answerOption.length >= 2) {
      // For switch fields with no answer, default to the second option (falseOption)
      // This ensures enableWhen conditions can evaluate correctly on first render
      initialValue = item.answerOption[1]?.valueCoding ?? null;
    } else {
      initialValue = answers?.[0] ?? INTAKE_FORM_INITIAL_VALUES[item.type] ?? null;
    }
    return { ...acc, [item.linkId]: initialValue };
  }, {});
};

export function getNestedFormAnswer(intakeForm: Record<string, unknown>, key: string): unknown {
  if (!key) return undefined;
  // Support dot notation for nested keys
  const parts = key.split(".");
  let value: unknown = intakeForm;
  for (const part of parts) {
    if (value == null || typeof value !== "object") return undefined;
    value = (value as Record<string, unknown>)[part];
  }
  return value;
}

export const getFormControlCodeableConcept = (questionnaireItem: QuestionnaireItem | undefined) => {
  return getExtensionValue(questionnaireItem, Extensions.QuestionnaireItemControl) as
    | CodeableConcept
    | undefined;
};

export const getFormControlType = (formControlCodeableConcept: CodeableConcept | undefined) => {
  return formControlCodeableConcept?.coding?.[0]?.code;
};

// Helper function to check if a value is meaningful
export const isMeaningfulValue = (value: unknown): boolean => {
  if (!value) return false;
  if (typeof value === "object" && Object.keys(value).length === 0) return false;
  return true;
};

// Helper function to check if a field is autopopulated
export const isAutopopulatedField = (item: QuestionnaireItem): boolean => {
  return (
    item.extension?.some(
      (ext) =>
        ext.url === "http://quiltedhealth.com/sd/questionnaire-field-autopopulate" &&
        ext.valueBoolean === true,
    ) || item.type === "time"
  );
};

// Helper function to recursively check values
export const checkValues = (
  items: QuestionnaireItem[],
  values: Record<string, unknown>,
): boolean => {
  for (const [key, value] of Object.entries(values)) {
    const item = items.find((i) => i.linkId === key);
    if (!item) continue;

    if (item.type === "group" && item.item) {
      // If this is a group, recursively check its values
      if (
        typeof value === "object" &&
        value !== null &&
        checkValues(item.item, value as Record<string, unknown>)
      ) {
        return true;
      }
    } else {
      // Skip if this is an autopopulated field
      if (isAutopopulatedField(item)) continue;

      // Check if the value is meaningful
      if (isMeaningfulValue(value)) {
        // For answer arrays, check if any answer has a meaningful value
        if (Array.isArray(value)) {
          if (
            value.some(
              (answer) =>
                answer.valueString ||
                answer.valueDecimal !== undefined ||
                answer.valueCoding ||
                answer.valueBoolean !== undefined,
            )
          ) {
            return true;
          }
        } else {
          return true;
        }
      }
    }
  }
  return false;
};

/**
 * Type-safe wrapper for form.setFieldValue in generic questionnaire components
 * where the form Values type T is unconstrained.
 *
 * @mantine/form v8 introduced FormPathValue which is an internal type not
 * exported from the public API. This helper casts through `unknown` (the
 * standard TypeScript non-`any` escape hatch) so call sites stay clean.
 *
 * @param form - The Mantine form instance
 * @param path - The dot-notation field path string
 * @param value - The new value to set
 */
export function setFormFieldValue<T>(
  form: UseFormReturnType<T>,
  path: string,
  value: unknown,
): void {
  (form as unknown as UseFormReturnType<Record<string, unknown>>).setFieldValue(path, value);
}

// Main function to check if a questionnaire response has meaningful inputs
export const hasMeaningfulInputs = (
  questionnaire: Pick<Questionnaire, "item">,
  values: Record<string, unknown>,
): boolean => {
  return checkValues(questionnaire.item || [], values);
};
